// @spec IAM-STR-012
// API client functions for organizations

import type { ApiClient } from '@/lib/api-client';

/**
 * Role information for a user's membership in an organization.
 */
export interface RoleInfo {
  id: string;
  name: string;
  isSystemRole: boolean;
}

/**
 * Parent organization information for subsidiaries.
 */
export interface ParentOrganizationInfo {
  id: string;
  name: string;
}

/**
 * Organization details as seen by a user member.
 */
export interface UserOrganization {
  id: string;
  authOrganizationId?: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  role: RoleInfo;
  memberCount: number;
  isActive: boolean;
  parentOrganization: ParentOrganizationInfo | null;
  createdAt: string;
  joinedAt: string;
  scope?: 'direct' | 'inherited';
  inheritedFromOrgId?: string | null;
}

/**
 * Response schema for the organizations list endpoint.
 */
export interface OrganizationsListResponse {
  organizations: UserOrganization[];
  totalCount: number;
}

/**
 * Request schema for creating a top-level organization.
 */
export interface CreateOrganizationInput {
  name: string;
  slug?: string;
  description?: string | null;
  logoUrl?: string | null;
  contactEmail?: string | null;
  industry?: string | null;
  employeeCount?: '1-10' | '11-50' | '51-200' | '201-500' | '501-1000' | '1001-5000' | '5000+' | null;
}

/**
 * Response schema for organization creation.
 */
export interface CreateOrganizationResponse {
  id: string;
  authOrganizationId: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

/**
 * Fetch all organizations the authenticated user is a member of.
 *
 * @param apiClient - Configured API client with auth token
 * @returns List of user's organizations with membership details
 *
 * @example
 * const { getToken } = useAuth();
 * const client = createApiClient({ getAuthToken: getToken });
 * const orgs = await fetchOrganizations(client);
 */
export async function fetchOrganizations(apiClient: ApiClient): Promise<OrganizationsListResponse> {
  return apiClient.get<OrganizationsListResponse>('/organizations');
}

/**
 * Create a top-level organization for the current user.
 */
export async function createOrganization(
  apiClient: ApiClient,
  input: CreateOrganizationInput
): Promise<CreateOrganizationResponse> {
  return apiClient.post<CreateOrganizationResponse>('/organizations', input);
}

// @spec IAM-STR-014
/**
 * Response schema for the leave organization endpoint.
 */
export interface LeaveOrganizationResponse {
  success: boolean;
  message: string;
  organizationId: string;
  organizationName: string;
  remainingOrganizations: number;
}

/**
 * Leave an organization.
 *
 * Removes the current user's membership from the specified organization.
 * Cannot leave if you are the only admin in the organization.
 *
 * @param apiClient - Configured API client with auth token
 * @param organizationId - The ID of the organization to leave
 * @returns Response with success status and remaining organization count
 *
 * @example
 * const { getToken } = useAuth();
 * const client = createApiClient({ getAuthToken: getToken });
 * const result = await leaveOrganization(client, 'org-123');
 * if (result.remainingOrganizations === 0) {
 *   // Redirect to onboarding
 * }
 */
export async function leaveOrganization(
  apiClient: ApiClient,
  organizationId: string
): Promise<LeaveOrganizationResponse> {
  return apiClient.delete<LeaveOrganizationResponse>(`/organizations/${organizationId}/membership`);
}

// ── Organization tree types and API ──────────────────────────

export interface OrgTreeNode {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  level: number;
  status: string;
  memberCount: number;
  parentOrganizationId: string | null;
  aggregatedReportingEnabled: boolean;
  children: OrgTreeNode[];
}

export interface OrgTreeResponse {
  tree: OrgTreeNode;
  totalNodes: number;
}

export async function fetchOrganizationTree(
  apiClient: ApiClient,
  organizationId: string,
): Promise<OrgTreeResponse> {
  return apiClient.get<OrgTreeResponse>(`/organizations/${organizationId}/tree`);
}

// ── Organization forest (peer roots) ─────────────────────────

export interface OrgForestTree {
  tree: OrgTreeNode;
  totalNodes: number;
}

export interface OrgForestResponse {
  trees: OrgForestTree[];
  totalRoots: number;
}

export async function fetchOrganizationForest(
  apiClient: ApiClient,
): Promise<OrgForestResponse> {
  return apiClient.get<OrgForestResponse>(`/organizations/forest`);
}

export interface ReparentOrganizationInput {
  newParentId: string | null;
}

export interface ReparentOrganizationResponse {
  id: string;
  name: string;
  newParentId: string | null;
  level: number;
  path: string | null;
  message: string;
}

export async function reparentOrganization(
  apiClient: ApiClient,
  organizationId: string,
  input: ReparentOrganizationInput,
): Promise<ReparentOrganizationResponse> {
  return apiClient.patch<ReparentOrganizationResponse>(
    `/organizations/${organizationId}/parent`,
    input,
  );
}

// ── Inherited access types and API ───────────────────────────

export interface InheritedMemberInfo {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  role: string | null;
  status: string;
  scope: string;
  inheritedFromOrgId: string | null;
  inheritedFromOrgName: string | null;
}

export interface InheritedMembersResponse {
  members: InheritedMemberInfo[];
  totalCount: number;
}

export async function fetchInheritedMembers(
  apiClient: ApiClient,
  organizationId: string,
): Promise<InheritedMembersResponse> {
  return apiClient.get<InheritedMembersResponse>(
    `/organizations/${organizationId}/inherited-members`,
  );
}

export async function revokeInheritedAccess(
  apiClient: ApiClient,
  organizationId: string,
  userId: string,
): Promise<{ success: boolean; message: string }> {
  return apiClient.post(`/organizations/${organizationId}/inherited-members/revoke`, {
    userId,
  });
}

// ── Organization members list types and API ──────────────────

export interface MemberOrgAccessItem {
  organizationId: string;
  organizationName: string | null;
  role: string | null;
  scope: string;
  status: string;
}

export interface OrgMemberInfo {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  userAvatarUrl: string | null;
  role: string | null;
  roleId: string | null;
  scope: 'direct' | 'inherited';
  status: string;
  inheritedFromOrgId: string | null;
  inheritedFromOrgName: string | null;
  joinedAt: string | null;
  organizationAccess: MemberOrgAccessItem[];
}

export interface OrgMembersListResponse {
  members: OrgMemberInfo[];
  totalCount: number;
}

export async function fetchOrgMembers(
  apiClient: ApiClient,
  organizationId: string,
): Promise<OrgMembersListResponse> {
  return apiClient.get<OrgMembersListResponse>(
    `/organizations/${organizationId}/members`,
  );
}

export async function restoreInheritedAccess(
  apiClient: ApiClient,
  organizationId: string,
  userId: string,
  parentOrgId: string,
): Promise<{ success: boolean; message: string }> {
  return apiClient.post(`/organizations/${organizationId}/inherited-members/restore`, {
    userId,
    parentOrgId,
  });
}

/**
 * Response from ensuring Clerk membership for org switching.
 */
export interface EnsureClerkMembershipResponse {
  success: boolean;
  message: string;
  authOrganizationId: string;
  alreadyMember: boolean;
}

/**
 * Ensure the current user has a Clerk membership for the target organization.
 *
 * Required before switching to organizations where the user has inherited
 * (not direct) database membership, since Clerk may not know about them.
 */
export async function ensureClerkMembership(
  apiClient: ApiClient,
  organizationId: string,
): Promise<EnsureClerkMembershipResponse> {
  return apiClient.post<EnsureClerkMembershipResponse>(
    `/organizations/${organizationId}/ensure-clerk-membership`,
    {},
  );
}
